import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';

// --- MOCK DATA FOR "ALTERNATIVE INDICATORS" ---
const FED_SENTIMENT_DATA = [
  { day: 'T-5', score: 45, label: 'Rumor' },
  { day: 'T-4', score: 42, label: 'Leak' },
  { day: 'T-3', score: 60, label: 'Nomination' },
  { day: 'T-2', score: 65, label: 'Reaction' },
  { day: 'T-1', score: 72, label: 'Analysis' },
  { day: 'Today', score: 68, label: 'Current' },
];

const REPO_DATA = [
  { date: 'Jan', volume: 2400 },
  { date: 'Feb', volume: 2100 },
  { date: 'Mar', volume: 1800 },
  { date: 'Apr', volume: 1600 }, // Warsh effect simulation (Balance sheet reduction)
  { date: 'May', volume: 1400 },
];

// --- COMPONENTS ---

// 1. GAUGE CARD (For "Hawkishness")
const GaugeCard = ({ title, score, theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '12px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  }}>
    <h3 style={{ fontSize: '1rem', color: theme.textSecondary, marginBottom: '1rem' }}>{title}</h3>
    
    <div style={{ position: 'relative', width: '200px', height: '100px', overflow: 'hidden' }}>
      {/* Gauge Background */}
      <div style={{ 
        width: '200px', height: '200px', borderRadius: '50%', 
        background: `conic-gradient(from 180deg, #4ade80 0deg, #facc15 90deg, #ef4444 180deg, transparent 180deg)`
      }} />
      {/* Needle */}
      <div style={{ 
        position: 'absolute', bottom: '0', left: '50%', 
        width: '4px', height: '90px', background: theme.textPrimary,
        transformOrigin: 'bottom center',
        transform: `translateX(-50%) rotate(${(score / 100) * 180 - 90}deg)`,
        transition: 'transform 1s ease-out'
      }} />
      {/* Cover */}
      <div style={{ 
        position: 'absolute', bottom: '-10px', left: '50%', 
        width: '20px', height: '20px', borderRadius: '50%', 
        background: theme.textPrimary, transform: 'translateX(-50%)' 
      }} />
    </div>
    
    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
      <span style={{ fontSize: '2rem', fontWeight: '800', color: theme.textPrimary }}>{score}%</span>
      <p style={{ fontSize: '0.8rem', color: score > 60 ? '#ef4444' : '#4ade80' }}>
        {score > 60 ? 'HAWKISH (Contractionary)' : 'DOVISH (Expansionary)'}
      </p>
    </div>
  </div>
);

// 2. CHART CARD (For Alternative Data)
const AltDataCard = ({ title, subtitle, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '12px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    height: '300px'
  }}>
    <div style={{ marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: theme.textPrimary }}>{title}</h3>
      <p style={{ fontSize: '0.8rem', color: theme.textSecondary }}>{subtitle}</p>
    </div>
    <div style={{ height: '80%', width: '100%' }}>
      {children}
    </div>
  </div>
);

// 3. ASSET CORRELATION ROW
const AssetRow = ({ ticker, name, correlation, rationale, price, change, theme }) => (
  <div style={{ 
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem', borderBottom: `1px solid ${theme.border}`,
    background: 'rgba(255,255,255,0.02)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 2 }}>
      <div style={{ 
        width: '40px', height: '40px', borderRadius: '8px', 
        background: correlation === 'Positive' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        color: correlation === 'Positive' ? '#4ade80' : '#ef4444',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
      }}>
        {correlation === 'Positive' ? 'LONG' : 'SHRT'}
      </div>
      <div>
        <div style={{ fontWeight: 'bold', color: theme.textPrimary }}>{ticker}</div>
        <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>{name}</div>
      </div>
    </div>
    
    <div style={{ flex: 3, fontSize: '0.9rem', color: theme.textSecondary, paddingRight: '1rem' }}>
      {rationale}
    </div>

    <div style={{ flex: 1, textAlign: 'right' }}>
      <div style={{ fontWeight: 'bold', color: theme.textPrimary }}>{price}</div>
      <div style={{ fontSize: '0.8rem', color: change.startsWith('+') ? '#4ade80' : '#ef4444' }}>{change}</div>
    </div>
  </div>
);

export default function ThesisWarshPage() {
  const [isDark, setIsDark] = useState(true);
  
  const theme = {
    background: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(240, 240, 240, 0.9)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#555',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#050505' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
       {/* Background */}
       <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Silk speed={20} scale={1.8} color={isDark ? "#1e293b" : "#e2e8f0"} noiseIntensity={isDark ? 5 : 2} rotation={0.5} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)} topTickers={[]} />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          
          {/* --- HERO: THESIS DEFINITION --- */}
          <div style={{ marginBottom: '3rem', borderBottom: `1px solid ${theme.border}`, paddingBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
               <span style={{ background: '#3b82f6', color: 'white', padding: '4px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: 'bold' }}>THESIS #042</span>
               <span style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>Updated: Feb 02, 2026</span>
            </div>
            <h1 style={{ fontSize: '3.5rem', fontWeight: '800', lineHeight: '1.1', marginBottom: '1.5rem', background: 'linear-gradient(to right, #fff, #94a3b8)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>
              Warsh Nomination:<br /> The "Pragmatic Hawk"
            </h1>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem' }}>
              <p style={{ fontSize: '1.2rem', color: theme.textSecondary, lineHeight: '1.6' }}>
                Kevin Warsh's nomination signals a potential regime shift. Markets are pricing a <strong>Stronger Dollar</strong> (less aggressive cuts) and a <strong>Steeper Yield Curve</strong> (term premium return).
                While politically aligned with Trump, Warsh's historical focus on "Sound Money" and deregulation creates a unique "High Growth / High Real Rates" environment.
              </p>
              
              {/* Thesis Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', border: `1px solid ${theme.border}`, borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>Probability</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4ade80' }}>85%</div>
                </div>
                <div style={{ padding: '1rem', border: `1px solid ${theme.border}`, borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>Regime</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#facc15' }}>Tightening</div>
                </div>
              </div>
            </div>
          </div>

          {/* --- BLOCK 1: ALTERNATIVE DATA (INDICATORS) --- */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>📡 Alternative Indicators</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
              
              {/* 1. Sentiment Gauge */}
              <GaugeCard title="Fed NLP Sentiment (Hawkishness)" score={68} theme={theme} />

              {/* 2. Repo Market Chart */}
              <AltDataCard 
                title="Liquidity Stress Monitor" 
                subtitle="Reverse Repo (ON RRP) Usage - Balance Sheet Proxy" 
                theme={theme}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={REPO_DATA}>
                    <defs>
                      <linearGradient id="colorVol" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke={theme.textSecondary} />
                    <YAxis stroke={theme.textSecondary} />
                    <Tooltip contentStyle={{ backgroundColor: '#111', borderColor: '#333' }} />
                    <Area type="monotone" dataKey="volume" stroke="#3b82f6" fillOpacity={1} fill="url(#colorVol)" />
                  </AreaChart>
                </ResponsiveContainer>
              </AltDataCard>

              {/* 3. Breakevens Chart */}
              <AltDataCard 
                title="Inflation Expectations (5y Breakeven)" 
                subtitle="Market pricing of Warsh's 'Sound Money' credibility" 
                theme={theme}
              >
                 <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={FED_SENTIMENT_DATA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="day" stroke={theme.textSecondary} />
                    <YAxis domain={[40, 80]} stroke={theme.textSecondary} />
                    <Tooltip contentStyle={{ backgroundColor: '#111', borderColor: '#333' }} />
                    <Line type="step" dataKey="score" stroke="#facc15" strokeWidth={2} dot={{r: 4}} />
                  </LineChart>
                </ResponsiveContainer>
              </AltDataCard>

            </div>
          </div>

          {/* --- BLOCK 2: ASSET ALLOCATION (THE BASKET) --- */}
          <div>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>💼 The Warsh Basket</h2>
            
            <div style={{ 
              background: theme.cardBg, 
              borderRadius: '12px', 
              border: `1px solid ${theme.border}`,
              overflow: 'hidden'
            }}>
              <div style={{ 
                padding: '1rem', 
                background: 'rgba(255,255,255,0.05)', 
                borderBottom: `1px solid ${theme.border}`,
                display: 'grid',
                gridTemplateColumns: '2fr 3fr 1fr',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: theme.textSecondary,
                textTransform: 'uppercase'
              }}>
                <div>Asset</div>
                <div>Thesis Logic</div>
                <div style={{ textAlign: 'right', paddingRight: '1rem' }}>Impact</div>
              </div>

              <AssetRow 
                ticker="DXY" 
                name="US Dollar Index" 
                correlation="Positive" 
                rationale="Higher real rates + 'Sound Money' rhetoric strengthens USD vs EM/Euro."
                price="105.40" 
                change="+1.2%" 
                theme={theme} 
              />
              <AssetRow 
                ticker="KRE" 
                name="Regional Banking ETF" 
                correlation="Positive" 
                rationale="Warsh is historically pro-deregulation and anti-Basel III endgame."
                price="$52.30" 
                change="+3.4%" 
                theme={theme} 
              />
               <AssetRow 
                ticker="TLT" 
                name="20+ Year Treasury" 
                correlation="Negative" 
                rationale="Bear Steepener: Long end yields rise as term premium returns."
                price="$94.10" 
                change="-1.5%" 
                theme={theme} 
              />
              <AssetRow 
                ticker="GLD" 
                name="Gold Trust" 
                correlation="Negative" 
                rationale="Gold struggles in high real-rate environments (Warsh fights inflation)."
                price="$2,015" 
                change="-0.8%" 
                theme={theme} 
              />
              <AssetRow 
                ticker="EWZ" 
                name="Brazil ETF" 
                correlation="Negative" 
                rationale="Strong Dollar draws liquidity away from Emerging Markets."
                price="$29.40" 
                change="-2.1%" 
                theme={theme} 
              />

            </div>
          </div>

        </main>
      </div>
    </div>
  );
}